import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { setProgress } from "../lib/localStore";
import { mediaUrl } from "../lib/media";
import { ChevronLeft, Play } from "lucide-react";
export default function Watch() {
    const params = useParams();
    const seriesId = params.seriesId;
    const id = params.id;
    // For /watch/episode/:seriesId/:id the :kind slot doesn't exist — derive it.
    const kind = params.kind || (seriesId ? "episode" : undefined);
    const [item, setItem] = useState(null);
    const [series, setSeries] = useState(null);
    const videoRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            if (kind === "episode") {
                const r = await api.get(`/series/${seriesId}`);
                setSeries(r.data);
                const ep = (r.data.episodes || []).find(e => e.id === id);
                setItem(ep ? { ...ep, series_title: r.data.title, series_id: r.data.id } : null);
            } else if (kind === "viral") {
                const r = await api.get(`/videos/viral/${id}`);
                setItem(r.data);
            } else if (kind === "ai") {
                const list = await api.get(`/ai-creative`);
                const found = list.data.find(x => x.id === id);
                setItem(found);
            }
        })();
    }, [kind, seriesId, id]);

    // Anonymous watch heartbeat -> analytics (must be declared before any early return)
    useEffect(() => {
        if (!item) return;
        let sid = localStorage.getItem("adk_session_id");
        if (!sid) {
            sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
            localStorage.setItem("adk_session_id", sid);
        }
        let lastTime = 0;
        let lastReport = 0;
        const flush = (force = false) => {
            const v = videoRef.current;
            if (!v || !v.duration) return;
            const now = v.currentTime;
            const delta = Math.max(0, now - lastTime);
            lastTime = now;
            const sinceReport = Date.now() - lastReport;
            if (!force && sinceReport < 12000) return; // every ~15s
            lastReport = Date.now();
            const percent = (now / v.duration) * 100;
            api.post("/analytics/watch", {
                entity_type: kind === "episode" ? "episode" : kind === "viral" ? "viral" : "ai",
                entity_id: id,
                series_id: kind === "episode" ? seriesId : null,
                session_id: sid,
                seconds_delta: delta,
                percent,
                duration: v.duration,
            }).catch(() => {});
        };
        const iv = setInterval(() => flush(false), 5000);
        const onVis = () => { if (document.visibilityState === "hidden") flush(true); };
        document.addEventListener("visibilitychange", onVis);
        return () => {
            clearInterval(iv);
            document.removeEventListener("visibilitychange", onVis);
            flush(true);
        };
    }, [item, kind, id, seriesId]);

    if (!item) return <div className="pt-32 mx-auto max-w-[1600px] px-6 text-white/60">Loading…</div>;

    const onTime = () => {
        const v = videoRef.current;
        if (!v || !v.duration) return;
        const percent = (v.currentTime / v.duration) * 100;
        const entryId = kind === "episode" ? `${seriesId}:${id}` : id;
        setProgress(entryId, {
            title: kind === "episode" ? `${item.series_title} — Part ${item.part_number}` : item.title,
            subtitle: kind === "episode" ? item.title : item.category,
            thumbnail: item.thumbnail,
            percent,
            type: kind,
            series_id: seriesId,
            link: window.location.pathname,
        });
    };

    const nextEpisode = series?.episodes?.find(e => !e.is_coming_soon && e.part_number === (item.part_number || 0) + 1);

    return (
        <div data-testid="watch-page" className="pt-20 pb-24 bg-black min-h-screen">
            <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
                <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-white/70 hover:text-[#FFD400] mb-6 transition-colors">
                    <ChevronLeft className="w-4 h-4" /> Back
                </button>

                <div className="aspect-video w-full rounded-[18px] overflow-hidden bg-black border border-white/10">
                    {item.video_url ? (
                        <video
                            ref={videoRef}
                            src={mediaUrl(item.video_url)}
                            poster={mediaUrl(item.thumbnail)}
                            controls
                            autoPlay
                            playsInline
                            preload="metadata"
                            onTimeUpdate={onTime}
                            className="w-full h-full object-contain bg-black"
                            data-testid="watch-video"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center flex-col gap-3 text-white/60" data-testid="watch-no-video">
                            <Play className="w-12 h-12 text-[#FFD400]" />
                            <p>No video source yet. Upload via Admin Dashboard.</p>
                        </div>
                    )}
                </div>

                <div className="mt-6 flex items-start justify-between gap-6 flex-wrap">
                    <div className="max-w-3xl">
                        {kind === "episode" && (
                            <div className="text-[11px] uppercase tracking-widest text-[#FFD400] font-bold">
                                {item.series_title} · Part {item.part_number}
                            </div>
                        )}
                        <h1 className="mt-1 font-display font-black text-3xl sm:text-4xl tracking-tight">{item.title}</h1>
                        <p className="mt-3 text-white/70 leading-relaxed">{item.description}</p>
                    </div>
                    {nextEpisode && (
                        <Link
                            to={`/watch/episode/${series.id}/${nextEpisode.id}`}
                            data-testid="watch-next-btn"
                            className="inline-flex items-center gap-2 bg-[#FFD400] text-black font-bold rounded-full px-5 py-3 hover:scale-105 transition-transform"
                        >
                            Next: Part {nextEpisode.part_number}
                        </Link>
                    )}
                </div>
            </div>
        </div>
    );
}
